import { Outlet } from 'react-router-dom'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { userPreferencesPort } from '@/infra/config/beans'
import { useTranslation } from 'react-i18next'
import { Suspense, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import FamilyCardListSkeleton from '@/src/components/global/family-card-list-skeleton/FamilyCardListSkeleton'
import { MonthFilterSelect } from '@/src/components/month-filter-select/MonthFilterSelect'
import { getCurrentMonthValue } from '@/src/utils/dateUtils'
import { getDiagnoses } from '@/infra/api/generated/diagnoses/diagnoses'
import { useTrackSection } from '@/src/analytics/hooks/use-track-section/useTrackSection'

const { getPriorityDiagnoses } = getDiagnoses()

export const priorityDiagnosesQuery = (storeId: number, teamId: number, periodValue: string) => ({
  queryKey: ['priorityDiagnoses', storeId, teamId, periodValue],
  queryFn: () =>
    getPriorityDiagnoses(storeId, teamId, {
      periodValue: periodValue,
    }),
})

const Layout = () => {
  const { t } = useTranslation('common', { keyPrefix: 'pages.priorities' })
  const { team, store } = useUserPreferences(userPreferencesPort)
  usePageHeaders(t('title'), `${team?.name}`)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => getCurrentMonthValue())

  useTrackSection()

  return (
    <div className="bg-white h-full overflow-auto flex flex-col p-5 gap-4">
      <MonthFilterSelect value={selectedMonth} onChange={setSelectedMonth} />
      <Suspense fallback={<PrioritySkeleton />}>
        <LayoutContent storeId={store!.id} teamId={team!.id} month={selectedMonth} />
      </Suspense>
    </div>
  )
}

const LayoutContent = ({
  storeId,
  teamId,
  month,
}: {
  storeId: number
  teamId: number
  month: string
}) => {
  const { data } = useSuspenseQuery(priorityDiagnosesQuery(storeId, teamId, month))
  return <Outlet key={month} context={{ data }} />
}

const PrioritySkeleton = () => {
  return <div className="flex flex-col gap-3">{<FamilyCardListSkeleton skeletonCount={3} />}</div>
}

export default Layout
